import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Input, Label } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  SidePanelCard,
  ToggleRow,
  LinkedCardRow,
} from "../../../components/Common/PageWidgets";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import CustomMultiSelector from "../../../components/AuthenticationComponents/CustomMultiSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import TopUpModal from "../../../components/Common/TopUpModal";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";

// Stripe
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

//Import API
import endpoints from "../../../api/endpoints";
import { get, post, put, postFormData, del, patch } from "../../../api/axios";

//Utils
import { extractErrorMessage } from "../../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import cardChipIcon from "../../../assets/images/support/stripe-account.svg";
import linkIcon from "../../../assets/images/support/link-white.svg";
import passwordIcon from "../../../assets/images/support/password-icon.svg";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";
import thumbPlaceholder from "../../../assets/images/user-dashboard/thumb-placeholder.svg";
import walletIcon from "../../../assets/images/sidebar/wallet-orange.svg";

/* -------------------------------------------------------------------------
 * RestaurantSettingsPage — editable restaurant profile fields, notification toggles, and
 * linked-account management.
 * ---------------------------------------------------------------------- */

// Validation schema using Yup
const restaurantProfileSchema = Yup.object().shape({
  title: Yup.string()
    .required("Restaurant name is required")
    .min(2, "Restaurant name must be at least 2 characters")
    .max(255, "Restaurant name must not exceed 255 characters"),
  legal_business_name: Yup.string()
    .max(255, "Legal business name must not exceed 255 characters"),
  email: Yup.string().required("Email is required").email("Please enter a valid email address"),
  mobile: Yup.string()
    .required("Phone number is required")
    .min(10, "Phone number must be at least 10 characters"),
  overview: Yup.string()
    .required("Overview is required")
    .min(10, "Overview must be at least 10 characters"),
  price_range: Yup.string().required("Price range is required"),
  address: Yup.string().required("Address is required"),
});

// Staff profile validation schema
const staffProfileSchema = Yup.object().shape({
  first_name: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(255, "First name must not exceed 255 characters"),
  last_name: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(255, "Last name must not exceed 255 characters"),
  email: Yup.string().required("Email is required").email("Please enter a valid email address"),
});

const RestaurantSettingsPage = ({
  profile,
  onProfileFieldChange,
  onEditPhoto,
  onDeletePhoto,
  onChangePassword,
  onSaveChanges,
}) => {
  document.title = "Restaurant Settings | Omaha Food Lovers";
  const navigate = useNavigate();

  const [restaurantProfile, setRestaurantProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: "",
    legal_business_name: "",
    email: "",
    mobile: "",
    overview: "",
    price_range: "$$",
    address: "",
  });
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [hasMoreCategories, setHasMoreCategories] = useState(true);
  const [categoryPage, setCategoryPage] = useState(1);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [linkedAccount, setLinkedAccount] = useState(null);
  const [addressComponents, setAddressComponents] = useState({
    city: '',
    state: '',
    country: '',
    zipcode: '',
    latitude: '',
    longitude: '',
  });
  const [weekHours, setWeekHours] = useState([
    { day: 'Monday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Tuesday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Wednesday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Thursday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Friday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Saturday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Sunday', open: '', closed: '', isClosed: false, id: null },
  ]);
  const [isOpen24Hours, setIsOpen24Hours] = useState(false);
  const [linkingAccount, setLinkingAccount] = useState(false);
  const [unlinkingAccount, setUnlinkingAccount] = useState(false);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showUnlinkConfirmModal, setShowUnlinkConfirmModal] = useState(false);
  const [notifications, setNotifications] = useState({
    push_notifications_enabled: true,
    email_notifications_enabled: true,
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const bannerInputRef = React.useRef(null);
  const addressInputRef = React.useRef(null);
  const autocompleteRef = React.useRef(null);
  const formikRef = React.useRef(null);
  const [stripe, setStripe] = useState(null);
  
  // Staff profile state
  const [staffProfile, setStaffProfile] = useState(null);
  const [staffInitialValues, setStaffInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [savingStaffProfile, setSavingStaffProfile] = useState(false);
  const [showStaffPasswordForm, setShowStaffPasswordForm] = useState(false);
  const [staffPasswordValues, setStaffPasswordValues] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });
  const [savingStaffPassword, setSavingStaffPassword] = useState(false);

  // Load Stripe with network status check
  useEffect(() => {
    const stripeKey = process.env.REACT_APP_STRIPE_KEY;
    if (stripeKey && navigator.onLine) {
      loadStripe(stripeKey)
        .then(setStripe)
        .catch((error) => {
          // Silently handle Stripe loading errors - don't show toast
          console.error('Failed to load Stripe:', error);
        });
    }
  }, []);

  // Load Google Places API script
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

    if (!window.google && apiKey) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        // Google Places API loaded successfully
      };
      
      script.onerror = () => {
        console.error('Failed to load Google Places API script');
        toastr.error('Failed to load Google Places API script');
      };

      // Handle loading errors
      window.gm_authFailure = () => {
        console.error('Google Maps API authentication failed');
        toastr.error('Google Maps API authentication failed. Please check your API key.');
      };

      document.body.appendChild(script);
    } else if (!apiKey) {
      console.error('Google Places API key is missing');
      toastr.error('Google Places API key is missing. Please add REACT_APP_GOOGLE_PLACES_API_KEY to .env file');
    }
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places && addressInputRef.current) {
        // Clear existing autocomplete if any
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            types: ['establishment', 'geocode'],
            fields: ['formatted_address', 'address_components', 'geometry']
          }
        );

        autocompleteRef.current = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place && place.formatted_address) {
            // Update form value with the formatted address
            formikRef.current?.setFieldValue('address', place.formatted_address);

            // Extract address components
            const components = place.address_components || [];
            let city = '';
            let state = '';
            let country = '';
            let zipcode = '';

            components.forEach(component => {
              const types = component.types || [];
              if (types.includes('locality') || types.includes('sublocality')) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                state = component.long_name;
              } else if (types.includes('country')) {
                country = component.long_name;
              } else if (types.includes('postal_code')) {
                zipcode = component.long_name;
              }
            });

            // Extract geometry (latitude, longitude)
            const geometry = place.geometry || {};
            const location = geometry.location || {};
            const latitude = location.lat ? location.lat() : '';
            const longitude = location.lng ? location.lng() : '';

            // Update address components state
            setAddressComponents({
              city,
              state,
              country,
              zipcode,
              latitude: String(latitude),
              longitude: String(longitude),
            });
          }
        });
      }
    };

    // Wait a bit for the component to render
    const timer = setTimeout(() => {
      // Initialize immediately if Google API is loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
      } else {
        // Wait for Google API to load
        const checkGoogleLoaded = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkGoogleLoaded);
            initAutocomplete();
          }
        }, 100);

        return () => clearInterval(checkGoogleLoaded);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Update preview when banner file changes
  useEffect(() => {
    if (bannerFile) {
      const previewUrl = URL.createObjectURL(bannerFile);
      setBannerPreview(previewUrl);
      return () => URL.revokeObjectURL(previewUrl);
    } else {
      setBannerPreview(null);
    }
  }, [bannerFile]);

  // Fetch categories for multi-select with pagination
  const fetchCategories = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingCategories(true);
    } else {
      setLoadingCategories(true);
    }

    try {
      const response = await get(`${endpoints.listingCategories}?page=${pageNum}&per_page=20`);
      if (response.data?.data) {
        const data = response.data.data;
        const rawCategories = data.data || [];

        const categoryOptions = rawCategories.map((category) => ({
          value: category.id,
          label: category.title || `Category ${category.id}`,
        }));

        if (isLoadMore) {
          setCategories((prev) => [...prev, ...categoryOptions]);
        } else {
          setCategories(categoryOptions);
        }

        setHasMoreCategories(data.current_page < data.last_page);
        setCategoryPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleLoadMoreCategories = () => {
    if (!loadingCategories && hasMoreCategories) {
      fetchCategories(categoryPage + 1, true);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type - only allow image files
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        toastr.error("Only image files (JPEG, PNG, GIF, WebP, SVG) are allowed");
        e.target.value = ''; // Clear the input
        return;
      }
      setBannerFile(file);
    }
  };

  const handleEditBanner = () => {
    bannerInputRef.current?.click();
  };

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch restaurant profile from API
  const fetchProfile = useCallback(async () => {
    try {
      const response = await get(endpoints.restaurantMe);

      if (response.data.success) {
        const data = response.data.data;
        const user = data.user;
        const restaurant = data.current_restaurant;

        setRestaurantProfile(restaurant);

        // Set staff profile from user data
        setStaffProfile({
          ...data,
          userId: user?.id,
        });
        
        setStaffInitialValues({
          first_name: String(user?.first_name || ""),
          last_name: String(user?.last_name || ""),
          email: String(user?.email || ""),
        });

        // Set selected categories
        const categoryIds = restaurant?.categories?.map((cat) => cat.id) || [];
        setSelectedCategories(categoryIds);

        // Set banner preview
        setBannerPreview(restaurant?.banner_url || null);

        // Set address components
        setAddressComponents({
          city: String(restaurant?.address?.city || ""),
          state: String(restaurant?.address?.state || ""),
          country: String(restaurant?.address?.country || ""),
          zipcode: String(restaurant?.address?.zipcode || ""),
          latitude: String(restaurant?.address?.latitude || ""),
          longitude: String(restaurant?.address?.longitude || ""),
        });

        // Set week hours from API response (listing_time)
        if (restaurant?.listing_time && Array.isArray(restaurant.listing_time)) {
          const dayMap = {
            'monday': 'Monday',
            'tuesday': 'Tuesday',
            'wednesday': 'Wednesday',
            'thursday': 'Thursday',
            'friday': 'Friday',
            'saturday': 'Saturday',
            'sunday': 'Sunday'
          };
          
          const initialWeekHours = [
            { day: 'Monday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Tuesday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Wednesday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Thursday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Friday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Saturday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Sunday', open: '', closed: '', isClosed: false, id: null },
          ];
          
          const mappedHours = initialWeekHours.map(h => {
            const apiHour = restaurant.listing_time.find(ah => {
              const mappedDay = dayMap[ah.day?.toLowerCase()];
              return mappedDay === h.day;
            });
            if (apiHour) {
              if (apiHour.is_open_24_hours === 1) {
                return { ...h, open: '00:00', closed: '23:59', isClosed: false, id: apiHour.id };
              }
              if (apiHour.hours && apiHour.hours !== 'null-null' && apiHour.hours !== null) {
                const [open, closed] = apiHour.hours.split('-');
                return { ...h, open: open?.trim() || '', closed: closed?.trim() || '', isClosed: !open || !closed, id: apiHour.id };
              }
              return { ...h, open: '', closed: '', isClosed: true, id: apiHour.id };
            }
            return h;
          });
          
          setWeekHours(mappedHours);
          
          // Use the open_24_hours field from API response
          setIsOpen24Hours(restaurant?.open_24_hours === 1);
        }

        setInitialValues({
          title: String(restaurant?.title || ""),
          legal_business_name: String(restaurant?.legal_business_name || ""),
          email: String(restaurant?.email || ""),
          mobile: String(restaurant?.mobile || ""),
          overview: String(restaurant?.overview || ""),
          price_range: String(restaurant?.price_range || "$$"),
          address: String(restaurant?.address?.address || ""),
        });
        
        // Store user ID for staff profile fetching
        setStaffProfile(prev => ({ ...prev, userId: user.id }));
      }
    } catch (err) {
      console.error("Failed to fetch restaurant profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    }
  }, []);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch Stripe account status
  const fetchStripeAccountStatus = useCallback(async () => {
    try {
      const response = await get(endpoints.restaurantStripeConnectAccountsRefresh);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setStripeAccountStatus({
          account_id: data.account_id,
          onboarding_status: data.onboarding_status,
          payouts_enabled: data.payouts_enabled || false,
          charges_enabled: data.charges_enabled || false,
        });
        
        // If account is fully activated, set linked account
        if (data.onboarding_status === 'completed' && data.payouts_enabled && data.charges_enabled) {
          setLinkedAccount({
            stripe_account_id: data.account_id,
            name: 'Stripe Account',
            last4: '****',
            cashbackPercent: 0,
            processor: 'Stripe',
          });
        }
      }
    } catch (err) {
      // Account might not exist yet, which is fine
      setStripeAccountStatus(null);
    }
  }, []);

  // Check account status on mount
  useEffect(() => {
    fetchStripeAccountStatus();
  }, [fetchStripeAccountStatus]);

  // Fetch notification settings
  const fetchNotificationSettings = useCallback(async () => {
    try {
      const response = await get(endpoints.restaurantNotificationSettings);
      if (response.data.success && response.data.data) {
        setNotifications({
          push_notifications_enabled: response.data.data.push_notifications_enabled,
          email_notifications_enabled: response.data.data.email_notifications_enabled,
        });
      }
    } catch (err) {
      console.error("Failed to fetch notification settings:", err);
    }
  }, []);

  // Fetch notification settings on mount
  useEffect(() => {
    fetchNotificationSettings();
  }, [fetchNotificationSettings]);

  // Generate new activation link if the previous one failed or expired
  const handleGenerateActivationLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await get(endpoints.restaurantStripeConnectAccountsActivationLink);
      
      if (response.data.success && response.data.data.activation_url) {
        // Redirect to Stripe onboarding
        window.location.href = response.data.data.activation_url;
      } else {
        toastr.error("Failed to generate activation link");
      }
    } catch (err) {
      console.error("Failed to generate activation link:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setGeneratingLink(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setSaving(true);
    try {
      let response;

      // Transform week hours to API format
      const dayMap = {
        'Monday': 'monday',
        'Tuesday': 'tuesday',
        'Wednesday': 'wednesday',
        'Thursday': 'thursday',
        'Friday': 'friday',
        'Saturday': 'saturday',
        'Sunday': 'sunday'
      };

      const workingHours = weekHours.map(h => {
        const hasValidTimes = h.open && h.closed && h.open.trim() !== '' && h.closed.trim() !== '';
        const shouldMarkClosed = h.isClosed || !hasValidTimes;
        
        return {
          day: dayMap[h.day] || h.day.toLowerCase(),
          open: (shouldMarkClosed || isOpen24Hours) ? null : h.open,
          close: (shouldMarkClosed || isOpen24Hours) ? null : h.closed,
          is_closed: isOpen24Hours ? false : shouldMarkClosed,
          is_open_24_hours: isOpen24Hours
        };
      });

      const payload = {
        restaurant_name: values.title,
        legal_business_name: values.legal_business_name,
        email: values.email,
        phone: values.mobile,
        overview: values.overview,
        price_range: values.price_range,
        exclusive_description: null,
        category_ids: selectedCategories,
        working_hours: workingHours,
        address: values.address,
        city: addressComponents.city,
        state: addressComponents.state,
        country: addressComponents.country,
        zipcode: addressComponents.zipcode,
        latitude: addressComponents.latitude || null,
        longitude: addressComponents.longitude || null,
      };

      if (bannerFile) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append("form", JSON.stringify(payload));
        formData.append("banner", bannerFile);
        response = await postFormData(endpoints.restaurantDashboardProfile, formData);
      } else {
        // No file upload - regular POST
        response = await post(endpoints.restaurantDashboardProfile, payload);
      }

      if (response.data.success) {
        toastr.success("Restaurant profile updated successfully!");
        setBannerFile(null);
        await fetchProfile();
      }
    } catch (err) {
      console.error("Failed to update restaurant profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  // Handle link account
  const handleLinkAccount = async () => {
    setLinkingAccount(true);
    try {
      const response = await post(endpoints.restaurantStripeConnectAccounts, {});
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Update the account status
        setStripeAccountStatus({
          account_id: data.account_id,
          onboarding_status: data.onboarding_status,
          payouts_enabled: data.payouts_enabled || false,
          charges_enabled: data.charges_enabled || false,
        });
        
        if (data.activation_url) {
          // Redirect to Stripe onboarding
          window.location.href = data.activation_url;
        } else if (data.onboarding_status === 'completed' && data.payouts_enabled && data.charges_enabled) {
          // Account is already fully activated
          toastr.success("Stripe account already activated!");
          setLinkedAccount({
            stripe_account_id: data.account_id,
            name: 'Stripe Account',
            last4: '****',
            cashbackPercent: 0,
            processor: 'Stripe',
          });
        } else {
          // Account exists but no activation URL, try to generate one
          toastr.info("Account exists but requires completion. Generating activation link...");
          handleGenerateActivationLink();
        }
      } else {
        toastr.error("Failed to initiate account linking");
      }
    } catch (err) {
      console.error("Failed to link account:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setLinkingAccount(false);
    }
  };

  // Handle unlink account
  const handleUnlinkAccount = async () => {
    if (!linkedAccount?.stripe_account_id) return;
    setShowUnlinkConfirmModal(true);
  };

  // Confirm unlink account
  const confirmUnlinkAccount = async () => {
    if (!linkedAccount?.stripe_account_id) return;
    
    setUnlinkingAccount(true);
    setShowUnlinkConfirmModal(false);
    try {
      const url = endpoints.restaurantStripeConnectAccount.replace('{{connect_account_id}}', linkedAccount.stripe_account_id);
      const response = await del(url);
      
      if (response.data.success) {
        toastr.success("Account unlinked successfully!");
        setLinkedAccount(null);
        setStripeAccountStatus(null);
      }
    } catch (err) {
      console.error("Failed to unlink account:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setUnlinkingAccount(false);
    }
  };

  // Handle notification toggle
  const handleToggleNotification = async (key, checked) => {
    const updatedNotifications = {
      ...notifications,
      [key]: checked,
    };
    setNotifications(updatedNotifications);
    setSavingNotifications(true);

    try {
      const response = await patch(endpoints.restaurantNotificationSettings, updatedNotifications);
      if (response.data.success) {
        toastr.success("Notification settings updated successfully!");
      } else {
        toastr.error("Failed to update notification settings");
        // Revert on failure
        setNotifications(notifications);
      }
    } catch (err) {
      console.error("Failed to update notification settings:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Revert on failure
      setNotifications(notifications);
    } finally {
      setSavingNotifications(false);
    }
  };

  // Handle staff profile update
  const handleStaffProfileUpdate = async (values) => {
    setSavingStaffProfile(true);
    try {
      const restaurantConnection = staffProfile?.restaurant_connections?.[0];
      const payload = {
        vendor_listing_id: restaurantConnection?.resturant_id,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        designation_type: restaurantConnection?.designation_type || 'staff',
      };

      const response = await put(
        endpoints.adminStaffProfile.replace('{{staff_id}}', staffProfile?.userId),
        payload
      );

      if (response.data?.success) {
        toastr.success("Profile updated successfully");
        // Redirect to profile page
        navigate("/restaurant/profile");
      }
    } catch (err) {
      console.error("Failed to update staff profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setSavingStaffProfile(false);
    }
  };

  // Handle staff password change
  const handleStaffPasswordChange = async (values) => {
    setSavingStaffPassword(true);
    try {
      const restaurantConnection = staffProfile?.restaurant_connections?.[0];
      const payload = {
        vendor_listing_id: restaurantConnection?.resturant_id,
        first_name: staffProfile?.user?.first_name,
        last_name: staffProfile?.user?.last_name,
        email: staffProfile?.user?.email,
        designation_type: restaurantConnection?.designation_type || 'staff',
        password: values.password,
        password_confirmation: values.password_confirmation,
      };

      const response = await put(
        endpoints.adminStaffProfile.replace('{{staff_id}}', staffProfile?.userId),
        payload
      );

      if (response.data?.success) {
        toastr.success("Password changed successfully");
        setShowStaffPasswordForm(false);
        setStaffPasswordValues({
          current_password: "",
          password: "",
          password_confirmation: "",
        });
      }
    } catch (err) {
      console.error("Failed to change staff password:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    } finally {
      setSavingStaffPassword(false);
    }
  };

  return (
    <>
      <div className="page-content settings-page">
        <Container fluid>
          
          {/* Profile Settings Section */}
          <div className="surface-card mb-4">
            <div className="profile-header">
              <div>
                <h2 className="profile-title">Profile Settings</h2>
                <p className="rp-card__subtitle" style={{ margin: 0 }}>Manage your profile information and account credentials.</p>
              </div>
            </div>
            
            <Formik
                  initialValues={staffInitialValues}
                  validationSchema={staffProfileSchema}
                  onSubmit={handleStaffProfileUpdate}
                  enableReinitialize={true}
                >
                  {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Form>
                      <div className="profile-layout">
                        <div className="fields-grid profile-fields">
                          <div>
                            <CustomInput
                              label="First Name"
                              name="first_name"
                              type="text"
                              value={values.first_name}
                              placeholder="Enter first name"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.first_name && !!errors.first_name}
                              errorText={errors.first_name}
                              className="white-bg"
                            />
                          </div>
                          <div>
                            <CustomInput
                              label="Last Name"
                              name="last_name"
                              type="text"
                              value={values.last_name}
                              placeholder="Enter last name"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.last_name && !!errors.last_name}
                              errorText={errors.last_name}
                              className="white-bg"
                            />
                          </div>
                          <div>
                            <CustomInput
                              label="Email"
                              name="email"
                              type="email"
                              value={values.email}
                              placeholder="Enter email"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.email && !!errors.email}
                              errorText={errors.email}
                              className="white-bg"
                              disabled
                            />
                          </div>
                          {showStaffPasswordForm && (
                            <div>
                              <CustomInput
                                label="Current Password"
                                name="current_password"
                                type="password"
                                value={staffPasswordValues.current_password}
                                onChange={(e) => setStaffPasswordValues({...staffPasswordValues, current_password: e.target.value})}
                                placeholder="Enter current password"
                                className="white-bg"
                              />
                            </div>
                          )}
                          {showStaffPasswordForm && (
                            <div>
                              <CustomInput
                                label="New Password"
                                name="password"
                                type="password"
                                value={staffPasswordValues.password}
                                onChange={(e) => setStaffPasswordValues({...staffPasswordValues, password: e.target.value})}
                                placeholder="Enter new password (min 8 characters)"
                                className="white-bg"
                              />
                            </div>
                          )}
                          {showStaffPasswordForm && (
                            <div>
                              <CustomInput
                                label="Confirm New Password"
                                name="password_confirmation"
                                type="password"
                                value={staffPasswordValues.password_confirmation}
                                onChange={(e) => setStaffPasswordValues({...staffPasswordValues, password_confirmation: e.target.value})}
                                placeholder="Confirm new password"
                                className="white-bg"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-actions">
                        <SecondaryButton 
                          label={showStaffPasswordForm ? "Cancel" : "Change Password"} 
                          variant={showStaffPasswordForm ? "light-grey" : "light-orange"} 
                          icon={showStaffPasswordForm ? cancelIcon : passwordIcon} 
                          onClick={() => {
                            setShowStaffPasswordForm(!showStaffPasswordForm);
                            setStaffPasswordValues({
                              current_password: "",
                              password: "",
                              password_confirmation: "",
                            });
                          }} 
                        />
                        {showStaffPasswordForm ? (
                          <SecondaryButton
                            label="Save Password"
                            variant="light-orange"
                            icon={passwordIcon}
                            onClick={() => handleStaffPasswordChange(staffPasswordValues)}
                            disabled={savingStaffPassword}
                            loading={savingStaffPassword}
                          />
                        ) : (
                          <SecondaryButton 
                            label="Update Profile" 
                            variant="orange" 
                            icon={tickIcon} 
                            type="submit" 
                            disabled={savingStaffProfile || isSubmitting} 
                            loading={savingStaffProfile || isSubmitting}
                          />
                        )}
                      </div>
                    </Form>
                  )}
                </Formik>
          </div>

          {/* Restaurant Settings Section */}
          <div className="surface-card">

            
            <div className="profile-header">
              <div>
                <h2 className="profile-title">Restaurant Settings</h2>
                <p className="rp-card__subtitle" style={{ margin: 0 }}>Manage restaurant profile information and preferences.</p>
              </div>
            </div>
            
            <Formik
                initialValues={initialValues}
                validationSchema={restaurantProfileSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
                innerRef={formikRef}
              >
                  {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
                    <Form>
                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <div className="mb-4">
                            <label className="form-label">Banner Image</label>
                            <div
                              className="rounded d-flex align-items-center justify-content-center position-relative"
                              style={{
                                width: "100%",
                                height: 200,
                                backgroundColor: "#f8f9fa",
                                border: "2px dashed #dee2e6",
                                cursor: "pointer",
                                overflow: "hidden",
                              }}
                              onClick={handleEditBanner}
                            >
                              {bannerPreview ? (
                                <img
                                  src={bannerPreview}
                                  alt="Banner Preview"
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.src = thumbPlaceholder;
                                  }}
                                />
                              ) : (
                                <div className="text-center text-muted">
                                  <div style={{ fontSize: "40px", marginBottom: "10px" }}>+</div>
                                  <div>Click to upload banner image</div>
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              ref={bannerInputRef}
                              className="d-none"
                              accept="image/*"
                              onChange={handleBannerFileChange}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="title"
                            label="Restaurant Name"
                            name="title"
                            type="text"
                            placeholder="Enter restaurant name"
                            value={values.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.title && !!errors.title}
                            errorText={errors.title}
                            className="white-bg"
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="legal_business_name"
                            label="Legal Business Name"
                            name="legal_business_name"
                            type="text"
                            placeholder="Enter legal business name"
                            value={values.legal_business_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.legal_business_name && !!errors.legal_business_name}
                            errorText={errors.legal_business_name}
                            className="white-bg"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="email"
                            label="Email"
                            name="email"
                            type="email"
                            placeholder="Enter email address"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.email && !!errors.email}
                            errorText={errors.email}
                            className="white-bg"
                            disabled
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="mobile"
                            label="Phone Number"
                            name="mobile"
                            type="text"
                            placeholder="Enter phone number"
                            value={values.mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.mobile && !!errors.mobile}
                            errorText={errors.mobile}
                            className="white-bg"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomSelector
                            id="price_range"
                            label="Price Range"
                            name="price_range"
                            placeholder="Select price range"
                            options={[
                              { value: "$", label: "$" },
                              { value: "$$", label: "$$" },
                              { value: "$$$", label: "$$$" },
                              { value: "$$$$", label: "$$$$" },
                            ]}
                            value={values.price_range}
                            onChange={(value) => setFieldValue("price_range", value)}
                            onBlur={handleBlur}
                            invalid={touched.price_range && !!errors.price_range}
                            errorText={errors.price_range}
                            className="white-bg"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <CustomInput
                            id="overview"
                            label="Overview"
                            name="overview"
                            type="textarea"
                            placeholder="Enter restaurant overview"
                            value={values.overview}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.overview && !!errors.overview}
                            errorText={errors.overview}
                            className="white-bg"
                            rows={4}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <CustomMultiSelector
                            id="categories"
                            label="Categories"
                            name="categories"
                            placeholder="Search and select categories"
                            options={categories}
                            value={selectedCategories}
                            onChange={setSelectedCategories}
                            loading={loadingCategories}
                            onLoadMore={handleLoadMoreCategories}
                            hasMore={hasMoreCategories}
                            className="white-bg"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <div className="custom-input-wrapper white-bg">
                            <div className={`custom-input-group ${touched.address && errors.address ? 'is-invalid' : ''}`}>
                              <Label htmlFor="address" className="custom-input-label">Address</Label>
                              <Input
                                id="address"
                                name="address"
                                type="text"
                                placeholder="Enter address (Google Places Autocomplete)"
                                value={values.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="custom-input-field"
                                innerRef={addressInputRef}
                              />
                            </div>
                            {touched.address && errors.address && (
                              <div className="custom-input-feedback show">{errors.address}</div>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col xl={12} lg={12} md={12} sm={12}>
                          <div className="custom-input-wrapper white-bg">
                            <div className="custom-input-group">
                              <Label className="custom-input-label">Operating Hours</Label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' }}>
                                <div
                                  onClick={() => {
                                    setIsOpen24Hours(!isOpen24Hours);
                                    if (!isOpen24Hours) {
                                      setWeekHours(weekHours.map(h => ({ ...h, open: '00:00', closed: '23:59', isClosed: false })));
                                    }
                                  }}
                                  style={{
                                    width: '44px',
                                    height: '24px',
                                    backgroundColor: isOpen24Hours ? '#ec421c' : '#dee2e6',
                                    borderRadius: '12px',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: '#fff',
                                      borderRadius: '50%',
                                      position: 'absolute',
                                      top: '2px',
                                      left: isOpen24Hours ? '22px' : '2px',
                                      transition: 'left 0.2s'
                                    }}
                                  />
                                </div>
                                <span style={{ fontWeight: '500' }}>Open 24 Hours</span>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {!isOpen24Hours && (
                        <Row>
                          {weekHours.map((hour, index) => (
                            <Col key={hour.day} xl={3} lg={4} md={6} sm={12}>
                              <div style={{ 
                                backgroundColor: '#fff', 
                                border: '1px solid #e5e7eb', 
                                borderRadius: '16px', 
                                padding: '16px', 
                                marginBottom: '16px',
                                transition: 'all 0.2s ease',
                                cursor: hour.isClosed ? 'default' : 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                if (!hour.isClosed) {
                                  e.currentTarget.style.borderColor = '#ec421c';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(236, 66, 28, 0.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              >
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px' }}>{hour.day}</div>
                                  <div style={{ marginBottom: '8px' }}>
                                    <Input
                                      type="time"
                                      value={hour.open}
                                      onChange={(e) => {
                                        const newHours = [...weekHours];
                                        newHours[index].open = e.target.value;
                                        setWeekHours(newHours);
                                      }}
                                      disabled={hour.isClosed}
                                      style={{
                                        width: '100%',
                                        backgroundColor: hour.isClosed ? '#f8f9fa' : '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '13px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onFocus={(e) => {
                                        if (!hour.isClosed) {
                                          e.currentTarget.style.borderColor = '#ec421c';
                                        }
                                      }}
                                      onBlur={(e) => {
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                      }}
                                    />
                                  </div>
                                  <div style={{ marginBottom: '8px' }}>
                                    <Input
                                      type="time"
                                      value={hour.closed}
                                      onChange={(e) => {
                                        const newHours = [...weekHours];
                                        newHours[index].closed = e.target.value;
                                        setWeekHours(newHours);
                                      }}
                                      disabled={hour.isClosed}
                                      style={{
                                        width: '100%',
                                        backgroundColor: hour.isClosed ? '#f8f9fa' : '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '13px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onFocus={(e) => {
                                        if (!hour.isClosed) {
                                          e.currentTarget.style.borderColor = '#ec421c';
                                        }
                                      }}
                                      onBlur={(e) => {
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                      }}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                    <Input
                                      type="checkbox"
                                      checked={hour.isClosed}
                                      onChange={(e) => {
                                        const newHours = [...weekHours];
                                        newHours[index].isClosed = e.target.checked;
                                        if (e.target.checked) {
                                          newHours[index].open = '';
                                          newHours[index].closed = '';
                                        }
                                        setWeekHours(newHours);
                                      }}
                                      style={{ width: '16px', height: '16px' }}
                                    />
                                    <span style={{ fontSize: '13px' }}>Closed</span>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}

                      <div className="form-actions">
                        <SecondaryButton label="Save Changes" variant="orange" icon={tickIcon} type="submit" disabled={saving || isSubmitting} loading={saving || isSubmitting} />
                      </div>
                    </Form>
                  )}
                </Formik>
          </div>

            <Row className="g-3 align-items-start">
              <Col xl={6}>
                <SidePanelCard title="Notification Settings">
                  <ToggleRow
                    label="Push Notifications"
                    checked={notifications.push_notifications_enabled}
                    onChange={(checked) => handleToggleNotification('push_notifications_enabled', checked)}
                    disabled={savingNotifications}
                  />
                  <ToggleRow
                    label="Email Notifications"
                    checked={notifications.email_notifications_enabled}
                    onChange={(checked) => handleToggleNotification('email_notifications_enabled', checked)}
                    disabled={savingNotifications}
                  />
                </SidePanelCard>
              </Col>

              <Col xl={6}>
                <SidePanelCard title="Account Settings">
                  {linkedAccount && stripeAccountStatus?.onboarding_status === 'completed' ? (
                    <div className="stripe-account-widget">
                      <LinkedCardRow icon={cardChipIcon} cardName={linkedAccount.name} last4={stripeAccountStatus.account_id?.slice(-4) || '****'} />
                      
                      <p className="linked-account-card__description">
                        Your Stripe bank account is connected. Powered securely by{" "}
                        {linkedAccount.processor}.
                      </p>
                      
                      <div className="stripe-account-widget__topup-section">
                        <div className="stripe-account-widget__topup-header">
                          <h4 className="stripe-account-widget__topup-title">Top Up Balance</h4>
                          <p className="stripe-account-widget__topup-description">Add funds to your Stripe connected account</p>
                        </div>
                        
                        <SecondaryButton 
                          label="Add Funds" 
                          variant="light-orange" 
                          icon={walletIcon} 
                          onClick={() => setShowTopUpModal(true)}
                        />
                      </div>
                      
                      <div className="stripe-account-widget__actions">
                        <SecondaryButton 
                          label="Unlink Account" 
                          variant="orange" 
                          icon={linkIcon} 
                          onClick={handleUnlinkAccount} 
                          disabled={unlinkingAccount}
                          loading={unlinkingAccount}
                        />
                      </div>
                    </div>
                  ) : stripeAccountStatus?.onboarding_status === 'requirements_due' ? (
                    <div className="stripe-account-widget stripe-account-widget--pending">
                      <div className="stripe-account-widget__status">
                        <div className="stripe-account-widget__status-indicator stripe-account-widget__status-indicator--pending"></div>
                        
                      </div>
                      
                      <div className="stripe-account-widget__message">
                        <p className="stripe-account-widget__message-title">Complete Your Setup</p>
                        <p className="stripe-account-widget__message-description">
                          Your Stripe account requires additional information to be activated. Please complete the onboarding process to start receiving settlements.
                        </p>
                      </div>
                      
                      <div className="stripe-account-widget__actions">
                        <SecondaryButton 
                          label="Complete Setup" 
                          variant="orange" 
                          icon={linkIcon} 
                          onClick={handleGenerateActivationLink} 
                          disabled={generatingLink || linkingAccount}
                          loading={generatingLink || linkingAccount}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="stripe-account-widget">
                      <p className="linked-account-card__description">
                        Connect your Stripe account to enable secure payments and settlements. This allows you to receive payments from customers directly to your restaurant.
                      </p>
                      
                      <SecondaryButton 
                    label="Link Account" 
                    variant="orange" 
                    icon={linkIcon} 
                    onClick={handleLinkAccount} 
                    disabled={linkingAccount}
                    loading={linkingAccount}
                  />                    </div>
                  )}
                </SidePanelCard>
              </Col>
            </Row>
          </Container>
        </div>

        {stripe && (
          <Elements stripe={stripe}>
            <TopUpModal
              isOpen={showTopUpModal}
              onClose={() => setShowTopUpModal(false)}
              vendorListingId={restaurantProfile?.id}
              onSuccess={() => {
                // Refresh account status after successful top-up
                fetchStripeAccountStatus();
              }}
            />
          </Elements>
        )}

        {/* Unlink Account Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showUnlinkConfirmModal}
          title="Unlink Account"
          message="Are you sure you want to unlink your Stripe account? This action cannot be undone."
          onCancel={() => setShowUnlinkConfirmModal(false)}
          onConfirm={confirmUnlinkAccount}
          confirmButtonLoading={unlinkingAccount}
        />
      </>
    );
};

RestaurantSettingsPage.propTypes = {
  profile: PropTypes.shape({
    photoUrl: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    zipCode: PropTypes.string,
    address: PropTypes.string,
  }).isRequired,
  onProfileFieldChange: PropTypes.func,
  onEditPhoto: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onChangePassword: PropTypes.func,
  onSaveChanges: PropTypes.func,
};

RestaurantSettingsPage.defaultProps = {
  onProfileFieldChange: () => {},
  onEditPhoto: () => {},
  onDeletePhoto: () => {},
  onChangePassword: () => {},
  onSaveChanges: () => {},
  profile: {
    photoUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80",
    firstName: "Alex",
    lastName: "Johnson",
    email: "alex.johnson@example.com",
    phone: "+1 (555) 123-4567",
    zipCode: "68102",
    address: "123 Main Street, Omaha, NE",
  },
};

export default RestaurantSettingsPage;
